import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { SpreadOverviewHeader } from "./spread-overview-header";
import { scrollIntoView } from "../../utils/dom";

import "./spread-overview.css";
import { SpreadOverviewItemContainer } from "./spread-overview-item-container";
import { useSpreads } from "../../hooks/useSpreads";

export interface IVerticalScrollableRef {
	scrollIntoView(index: number, animate?: boolean): void;
}

const SpreadOverviewContent = forwardRef<
	IVerticalScrollableRef,
	{
		selectedSpreadIndex: number | undefined;
		onSelectSpreadIndex: (index: number) => void;
	}
>(({ onSelectSpreadIndex, selectedSpreadIndex }, parentRef) => {
	const spreads = useSpreads();

	const containerRef = useRef<HTMLDivElement>(null);

	useImperativeHandle(
		parentRef,
		() => ({
			scrollIntoView: (index: number, animate = false) => {
				if (!containerRef.current) return;
				const selectedEl = containerRef.current.children[index];
				if (!selectedEl) return;

				scrollIntoView(selectedEl, {
					animate,
					// Top margin is greater so we can see a part of the previous spread
					margins: eXpress.math.margins(0, 120, 0, 40),
				});
			},
		}),
		[],
	);

	if (!spreads) return null;

	return (
		<div className="spread-overview_content" ref={containerRef}>
			{spreads.map((spread, index) => (
				<SpreadOverviewItemContainer
					key={spread.oid}
					spread={spread}
					isSelected={selectedSpreadIndex === index}
					onSelect={() => onSelectSpreadIndex(index)}
					canAddSpreads={spread.canInsertAfter()}
				/>
			))}
		</div>
	);
});

SpreadOverviewContent.displayName = "SpreadOverviewContent";

export const SpreadOverview = ({
	onClose,
	onSelectSpreadIndex,
	selectedSpreadIndex,
	onDeleteSelection,
	isDeleteEnabled,
	contentScrollRef,
}: {
	onClose: () => void;
	selectedSpreadIndex: number | undefined;
	onSelectSpreadIndex: (spreadIndex: number) => void;
	onDeleteSelection: () => void;
	isDeleteEnabled: boolean;
	contentScrollRef: React.RefObject<IVerticalScrollableRef>;
}) => {
	return (
		<div className="spread-overview">
			<SpreadOverviewHeader
				onClose={onClose}
				isDeleteEnabled={isDeleteEnabled}
				onDelete={onDeleteSelection}
			/>
			<SpreadOverviewContent
				onSelectSpreadIndex={onSelectSpreadIndex}
				selectedSpreadIndex={selectedSpreadIndex}
				ref={contentScrollRef}
			/>
		</div>
	);
};
